import React from 'react';
import { useReferenceCardQuery } from '../../common/queries/use-chatbot-query';
import {
  isGenericCard,
  isAvatarCard,
  isExternalCard,
  isLandscapeCard,
  ReferenceCard as ReferenceCardType,
} from '../../models/chatbot/types';
import GenericRefCard from './cards/GenericRefCard';
import AvatarRefCard from './cards/AvatarRefCard';
import ExternalRefCard from './cards/ExternalRefCard';
import LandscapeRefCard from './cards/LandscapeRefCard';

interface ReferenceCardProps {
  referenceType: 'content' | 'user' | 'topic' | 'external' | 'shortcut';
  referenceId: string;
  card?: ReferenceCardType; // Pre-loaded card data (new messages)
  onClick?: () => void;
}

class ReferenceCardErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('ReferenceCard render error:', error);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

/**
 * Renders a reference card with pre-loaded or fetched data
 *
 * If card data is pre-loaded (new messages), it renders immediately.
 * Otherwise, it falls back to fetching the card data via API (old messages).
 */
const ReferenceCard: React.FC<ReferenceCardProps> = ({
  referenceType,
  referenceId,
  card: preloadedCard,
  onClick,
}) => {
  // Only fetch if no pre-loaded card data and valid reference params
  const {
    data: fetchedCard,
    isLoading,
    isError,
  } = useReferenceCardQuery(referenceType, referenceId, {
    enabled: !preloadedCard && !!referenceType && !!referenceId,
  });

  // Use pre-loaded card if available, otherwise use fetched card
  const card = preloadedCard || fetchedCard;

  // Don't render anything while loading (only when fetching) or if there's an error
  if ((!preloadedCard && isLoading) || isError || !card) {
    return null;
  }

  const renderCard = () => {
    if (isLandscapeCard(card)) {
      return <LandscapeRefCard card={card} onClick={onClick} />;
    }

    if (isGenericCard(card)) {
      return <GenericRefCard card={card} onClick={onClick} />;
    }

    if (isAvatarCard(card)) {
      return <AvatarRefCard card={card} onClick={onClick} />;
    }

    if (isExternalCard(card)) {
      return <ExternalRefCard card={card} onClick={onClick} />;
    }

    return null;
  };

  return (
    <ReferenceCardErrorBoundary>{renderCard()}</ReferenceCardErrorBoundary>
  );
};

export default ReferenceCard;
